import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GraphQL
import {
  selectOrderWithActionBasicInfoById,
  selectActiveOperatorsAndVehicles,
} from '../../../../../gql/selectors/actions';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { buildRegisteredByOperators } from '../../../../../utils/registeredByOperators';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../../utils/draftScopes';
import { foreignCreationParentRedirect } from '../creationDraft';

const loadActionDetails = async (orderId: string, actionId: string) => {
  try {
    return await selectOrderWithActionBasicInfoById(orderId, actionId);
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Action order loading error:', err);
    return false;
  }
};

// Operarios para el dropdown "Registrado por" (quién rellena la actuación/
// accidente). MISMO origen que `NewAction/loader.ts` (operarios activos del
// tenant + el usuario logueado si no es Tablet). Sin esto, la básica del RV
// —que usa este loader, no `NewActionLoader`— dejaba el dropdown vacío
// ("No se encontraron resultados"), feedback usuario 2026-07-24.
const loadRegisteredByOperators = async (userData: AuthContextType | null) => {
  try {
    const { operators } = await selectActiveOperatorsAndVehicles(true);
    return buildRegisteredByOperators(operators, userData);
  } catch {
    return buildRegisteredByOperators([], userData);
  }
};

export const LoaderActionPageOrder =
  (userData: AuthContextType | null): LoaderFunction =>
  async ({ params, request }) => {
    // Un borrador de OTRO padre no se abre bajo esta URL: atrás, una URL
    // compartida o el «Editar» de un resumen viejo enseñaban — y guardaban — el
    // borrador de la OT Y bajo la de la X (auditoría de creación 2.3).
    const foreignParent = foreignCreationParentRedirect({ params, request });
    if (foreignParent) return foreignParent;
    // Las copias de las pestañas, la lista de fotos y el croquis son del
    // borrador en curso (`draftId`): si eran de otro, fuera, antes de que nadie
    // las lea. Síncrono y antes de cualquier `await`: el loader de `details` y el
    // de la pestaña corren en paralelo (spec 2026-11-02 wizard-draft-ownership).
    const draftId = readCreationDraftId();
    claimDraftScope(ACTION_CREATION_SCOPE, draftId);
    claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

    // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
    // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
    // el router termine la navegación al instante y el `<Fallback>` enseñe el
    // Loader DENTRO de la pantalla nueva.
    const load = async () => {
    const registeredByOperators = await loadRegisteredByOperators(userData);

    const stringifyForm = sessionStorage.getItem(
      formActionNaming.NEW_ACTION_DATA
    );

    if (stringifyForm) {
      const sessionStorageData = JSON.parse(
        decompressSessionStorage(stringifyForm)
      );

      // Offline: no orderId/actionId yet — build minimal data from sessionStorage
      if (sessionStorageData._isOffline || !sessionStorageData.orderId) {
        return {
            pkInit: sessionStorageData.pkInit || '',
            pkEnd: sessionStorageData.pkEnd || '',
            orderDescription: sessionStorageData.description || '',
            actionDescription: sessionStorageData.notes || '',
            operators: registeredByOperators,
          };
      }

      const data = await loadActionDetails(
        sessionStorageData.orderId,
        sessionStorageData.actionId
      );
      return { ...(data || {}), operators: registeredByOperators };
    }

    return { operators: registeredByOperators };
  };

    return { data: load() };
  };
